import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate, NavLink, useParams } from "react-router-dom";
import API from "../../services";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
    FaTelegramPlane,
    FaWhatsapp,
    FaEnvelope,
} from "react-icons/fa";
import { IoLogoFacebook, IoLogoTwitter } from "react-icons/io";
import { UserDetails } from "../../models/UserDetails";
import { connect } from "react-redux";
import { RootState } from "../../models/RootState";
import { toast } from "react-toastify";
import Site from "../pages/Site";
import { BannerNavigation, getDynamicTextColors } from "../../store/authActions";
import { domain } from "../../constants/Branding";
import { getEnvVariable } from "../../constants/whitelabelEnv";
import { SiteConfig } from "../../models/SiteConfig";
import { useTranslation } from "../../hooks/useTranslation";
import RaffleInfo from "./RaffleInfo";


interface PromotionsProps {
    userDetails: UserDetails;
    siteConfig: SiteConfig;
}

const BrandName = getEnvVariable(domain, 'BRAND_NAME');
const showSites = typeof BrandName === 'string' && BrandName.toLowerCase() !== 'winfast99' && (BrandName.toLowerCase() === 'hindu777' || BrandName.toLowerCase() === 'lotus365');

const Promotions: React.FC<PromotionsProps> = ({ userDetails, siteConfig }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const banner = location.state?.banner;
    const [loading, setLoading] = useState<boolean>(true);
    const [bannerData, setBannerData] = useState<any>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    const [activeTab, setActiveTab] = useState<string>(showSites ? "Sites" : "Promotions");
    const [bannersData, setBannersData] = useState<any>(null);
    const locState = location.state as
        | { Pagetab?: string; raffleId?: string | number; raffle_id?: string | number }
        | null
        | undefined;
    const { Pagetab } = locState || {};
    const deeplinkRaffleId = locState?.raffleId ?? locState?.raffle_id;
    
    // Get dynamic text color classes based on different backgrounds
    const { pageTextColorClass, inputBoxTextColorClass, tabTextColorClass, activeTabTextColorClass } = getDynamicTextColors(userDetails);

    const handleTabClick = (tab: any) => {
        setActiveTab(tab);
        if (tab === "Promotions") {
            getBannerInfo();
        }
    };


    const getBannerInfo = async () => {
        setLoading(true);
        let response;
        try {
            response = await API.post(`/webapp/get-promotions`, {
                category: "PROMO"
            });
        } catch (e) {
            console.log(e);
            setLoading(false);
            return;
        }
        if (response && response.data.promotions) {
            setBannersData(response.data.promotions);
        }
        setLoading(false);
    };



    useEffect(() => {
        if (Pagetab === "site") {
            handleTabClick("Sites");
        } else if (Pagetab === "raffles") {
            handleTabClick("Raffles");
        } else {
            getBannerInfo();
        }
    }, []);

    const handleBannerClick = (banner: any) => {
        if (banner?.redirectionPath && banner.redirectionPath.trim() !== "") {
            BannerNavigation(banner.redirectionPath, navigate);
            return;
        }
        navigate('/bannerinfo', { state: { banner } });

    };

    const setPromoMessage = () => {
        try {
            const contentElement = document.getElementById("promo-message");
            if (contentElement) {
                contentElement.innerHTML = bannerData?.promoMessage || "<p>Stay tuned! Exciting promotions are on the way.</p>";
            } else {
                console.error("Element with ID 'promo-message' not found.");
            }
        } catch (err) {
            console.log(err);
        }
    };
    useEffect(() => {
        if (bannerData) {
            setPromoMessage();
        }
    }, [bannerData]);

    const promosTab = {
            key: "Promotions",
        label: t("promotions.title"),
        content: (
            <>
                {loading ? (
                    <div className="flex justify-center items-center py-8">
                        <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${pageTextColorClass === 'dark-text' ? 'border-black' : 'border-white'}`}></div>
                    </div>
                ) : bannersData && bannersData?.length > 0 ? (
                    <div className="space-y-4 pt-4">
                        {bannersData?.map((banner: any, index: number) => (
                            <div
                                key={index}
                                className="bg-inputbox-color rounded-lg overflow-hidden cursor-pointer hover:bg-gray-700 transition-colors"
                                onClick={() => {
                                    handleBannerClick(banner);
                                }}
                            >
                                {banner?.promotionImage && (
                                    <div className="w-full">
                                        <img
                                            src={banner?.promotionImage}
                                            alt={banner?.promotionTitle || t("promotions.promotionBanner")}
                                            className="w-full h-[170px]"
                                            onError={(e) => {
                                                const target = e.target as HTMLImageElement;
                                                target.style.display = 'none';
                                            }}
                                        />
                                    </div>
                                )}
                                {banner?.promotionTitle && (
                                    <div className="p-2">
                                        <h3 className={`font-semibold text-base text-center ${inputBoxTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}>
                                            {banner?.promotionTitle}
                                        </h3>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <p className={`text-lg ${pageTextColorClass === 'dark-text' ? 'text-gray-700' : 'text-gray-400'}`}>{t("promotions.noPromotionsAvailable")}</p>
                        <p className={`text-sm mt-2 ${pageTextColorClass === 'dark-text' ? 'text-gray-600' : 'text-gray-500'}`}>{t("promotions.checkBackLater")}</p>
                    </div>
                )}
            </>
        )
    };

    const rafflesTab = {
            key: "Raffles",
        label: t("promotions.raffles"),
        content: (
            <>
                <RaffleInfo isTabView deeplinkRaffleId={deeplinkRaffleId} />
            </>
        )
    };

    const sitesTab = {
            key: "Sites",
        label: t("promotions.sites"),
        content: (
            <>
                <Site />
            </>
        )
    };

    const win = typeof BrandName === 'string' && BrandName === 'Winfast99';
    const shouldShowSitesTab = siteConfig?.isSitesEnabled && !win;
    
    const tabs = showSites ? [
       ...(shouldShowSitesTab ? [sitesTab] : []),
         promosTab,
         rafflesTab] : [promosTab, rafflesTab, ...(shouldShowSitesTab ? [sitesTab] : [])];

    return (
        <>
            <div className="flex flex-col mt-4 px-4 w-full pb-[6rem] py-[4rem]">
                <div className="bonus-tabs">
                    <div className="bonus-nav">
                        <div className="navi navi-pills flex flex-nowrap w-full overflow-x-auto no-scrollbar" role="tablist">
                            {tabs.map((tab) => (
                                <div className="navi-item flex-1 min-w-0" key={tab.key}>
                                    <a
                                        role="tab"
                                        className={`navi-link text-center whitespace-nowrap px-2 py-2 text-xs sm:text-sm ${activeTab === tab.key ? `active ${activeTabTextColorClass}` : tabTextColorClass}`}
                                        onClick={() => handleTabClick(tab.key)}
                                    >
                                        {tab.label}
                                    </a>
                                </div>
                            ))}
                        </div>

                        <div className="tab-content">
                            {tabs.map((tab) => (
                                <div
                                    key={tab.key}
                                    className={`tab-pane fade ${activeTab === tab.key ? "show active" : ""}`}
                                    id={`tabs-tabpane-${tab.key}`}
                                    role="tabpanel"
                                >
                                    {activeTab === tab.key && tab.content}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

const mapStateToProps = (state: RootState) => {
    return {
        userDetails: state.userDetails?.userDetails,
        siteConfig: state.userDetails?.siteConfig,
    };
};

export default connect(mapStateToProps)(Promotions);


